/**
 * Create Child Key Modal Component (September 2025)
 * 
 * Modal component for creating child API keys with configurable credits and metadata.
 * Uses authenticated service key to create child keys via HTTP API endpoints.
 */

import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { getConvexHttpUrl } from '../utils/convex';

interface CreateChildKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceKey: string;
  onKeyCreated?: (keyData: { 
    apiKey: { 
      name: string; 
      keyHash: string; 
      environment: string; 
      rateLimitPerMinute: number 
    }; 
    message: string 
  }) => void;
}

interface CreateChildKeyRequest {
  name: string;
  environment: 'development' | 'staging' | 'production';
  rateLimitPerMinute?: number;
  metadata?: Record<string, unknown>;
}

// Helper function to generate work-safe kebab-case names with random two-digit number
const generateKeyName = (): string => {
  const nouns = ['tiger', 'eagle', 'mountain', 'river', 'forest', 'ocean', 'valley', 'summit', 'bridge', 'tower', 'garden', 'meadow'];
  const verbs = ['builds', 'creates', 'manages', 'monitors', 'tracks', 'connects', 'powers', 'guards', 'enables', 'supports', 'handles', 'processes'];
  
  const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
  const randomVerb = verbs[Math.floor(Math.random() * verbs.length)];
  const randomNumber = Math.floor(Math.random() * 90) + 10; // Generate 10-99
  
  return `${randomNoun}-${randomVerb}-${randomNumber}`;
};

export function CreateChildKeyModal({ 
  isOpen, 
  onClose, 
  serviceKey, 
  onKeyCreated 
}: CreateChildKeyModalProps) {
  const [formData, setFormData] = useState<{
    name: string;
    rateLimitPerMinute: number;
    credits: number;
    features: string[];
  }>({
    name: generateKeyName(),
    rateLimitPerMinute: 10,
    credits: 1000,
    features: ['credit_deductions', 'balance_checks', 'usage_analytics', 'notifications'],
  });

  // API call to create child key
  const createChildKeyMutation = useMutation({
    mutationFn: async (data: CreateChildKeyRequest) => {
      const response = await fetch(`${getConvexHttpUrl()}/api/keys/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': serviceKey,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to create child key: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: (data) => {
      console.log('Child key created successfully:', data);
      onKeyCreated?.(data);
      handleClose();
    },
    onError: (error) => {
      console.error('Failed to create child key:', error);
    },
  });

  const handleClose = () => {
    setFormData({
      name: generateKeyName(),
      rateLimitPerMinute: 10,
      credits: 1000,
      features: ['credit_deductions', 'balance_checks', 'usage_analytics', 'notifications'],
    });
    onClose();
  };

  const handleFeatureToggle = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature) 
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Build metadata object
    const metadata = {
      features: formData.features,
      configuration: {
        maxDailyRequests: formData.rateLimitPerMinute * 24 * 60,
        allowedEndpoints: ['/api/credits/deduct', '/api/credits/balance'],
        alertThresholds: {
          lowCredit: Math.floor(formData.credits * 0.1),
          dailyLimit: Math.floor(formData.rateLimitPerMinute * 24 * 60 * 0.8)
        }
      },
      allocatedCredits: formData.credits,
      createdBy: 'ui_dashboard',
      version: '1.0.0'
    };

    const requestData: CreateChildKeyRequest = {
      name: formData.name.trim() || generateKeyName(),
      environment: 'production', // All child keys are production keys
      rateLimitPerMinute: formData.rateLimitPerMinute,
      metadata,
    };

    createChildKeyMutation.mutate(requestData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-2 px-2 pb-4 text-center sm:pt-4 sm:px-4 sm:pb-20 sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block align-middle bg-terminal-black border-2 border-terminal-orange rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-sm sm:my-8 sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-terminal-black px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                  <h3 className="text-base sm:text-lg leading-6 font-medium text-terminal-orange-bright mb-3 sm:mb-4 terminal-glow">
                    [ CREATE CHILD API KEY ]
                  </h3>
                  
                  <div className="space-y-3 sm:space-y-4">
                    {/* Key Name */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-terminal-orange mb-1">
                        Key Name (customization optional)
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., tiger-builds-42"
                          className="flex-1 border border-terminal-orange bg-terminal-black text-terminal-orange px-2 sm:px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-terminal-orange-bright focus:border-terminal-orange-bright placeholder-terminal-orange-dim"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, name: generateKeyName() }))}
                          className="px-2 sm:px-3 py-2 border border-terminal-orange-dim bg-terminal-black text-terminal-orange text-xs sm:text-sm hover:bg-terminal-orange-dark focus:outline-none focus:ring-2 focus:ring-terminal-orange"
                        >
                          🎲
                        </button>
                      </div>
                    </div>

                    {/* Rate Limit */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-terminal-orange mb-1">
                        Rate Limit (requests per minute)
                      </label>
                      <input
                        type="number"
                        value={formData.rateLimitPerMinute}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          rateLimitPerMinute: parseInt(e.target.value) || 10 
                        }))}
                        min="1"
                        max="1000"
                        className="w-full border border-terminal-orange bg-terminal-black text-terminal-orange px-2 sm:px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-terminal-orange-bright focus:border-terminal-orange-bright"
                      />
                    </div>

                    {/* Starting Credits Configuration */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-terminal-orange mb-1">
                        Set Starting Credits
                      </label>
                      <input
                        type="number"
                        value={formData.credits}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          credits: parseInt(e.target.value) || 50 
                        }))}
                        min="0"
                        max="1000"
                        className="w-full border border-terminal-orange bg-terminal-black text-terminal-orange px-2 sm:px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-terminal-orange-bright focus:border-terminal-orange-bright"
                      />
                      <p className="text-xs text-terminal-orange-dim mt-1">
                        Starting credits allocated to this child API key
                      </p>
                    </div>


                    {/* Features */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-terminal-orange mb-2">
                        Features
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {['credit_deductions', 'balance_checks', 'usage_analytics', 'notifications'].map(feature => (
                          <label key={feature} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.features.includes(feature)}
                              onChange={() => handleFeatureToggle(feature)}
                              className="h-3 w-3 sm:h-4 sm:w-4 text-terminal-orange focus:ring-terminal-orange border-terminal-orange bg-terminal-black rounded"
                            />
                            <span className="ml-2 text-xs sm:text-sm text-terminal-orange capitalize">
                              {feature.replace('_', ' ')}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-terminal-black border-t border-terminal-orange px-3 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={createChildKeyMutation.isPending}
                className="w-full inline-flex justify-center px-3 sm:px-4 py-2 text-sm sm:text-base font-medium sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed terminal-button"
              >
                {createChildKeyMutation.isPending ? '[ CREATING... ]' : '[ CREATE KEY ]'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="mt-2 sm:mt-3 w-full inline-flex justify-center px-3 sm:px-4 py-2 text-sm sm:text-base font-medium sm:mt-0 sm:ml-3 sm:w-auto border border-terminal-orange-dim bg-terminal-black text-terminal-orange hover:bg-terminal-orange-dark focus:outline-none focus:ring-2 focus:ring-terminal-orange"
              >
                [ CANCEL ]
              </button>
            </div>

            {/* Error Display */}
            {createChildKeyMutation.error && (
              <div className="bg-terminal-black border border-red-500 p-4 mx-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-red-500">❌</span>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-red-500">
                      [ ERROR CREATING CHILD KEY ]
                    </h4>
                    <div className="mt-2 text-sm text-red-400">
                      <p>{createChildKeyMutation.error.message}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}